import React, { useRef, useEffect } from 'react';

export interface ReelPlayerProps {
  /** SAS or direct video URL – stream in <video src>, do not fetch as blob. */
  videoUrl: string;
  /** Called when reel becomes visible (for seenIds). */
  onVisible?: () => void;
  /** Optional className for the wrapper. */
  className?: string;
  /** When true, play; when false, pause. If set, overrides IntersectionObserver for play/pause. */
  isActive?: boolean;
}

/**
 * Native <video> that autoplays when visible (or when isActive) and pauses when not.
 * Streams directly from videoUrl; no blob/base64 fetch.
 */
const ReelPlayer: React.FC<ReelPlayerProps> = ({
  videoUrl,
  onVisible,
  className = '',
  isActive,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasReportedVisible = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    hasReportedVisible.current = false;

    const describeMediaError = (error: MediaError | null): string => {
      if (!error) return 'Unknown media error (null MediaError).';
      const codeMap: Record<number, string> = {
        1: 'MEDIA_ERR_ABORTED',
        2: 'MEDIA_ERR_NETWORK',
        3: 'MEDIA_ERR_DECODE',
        4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
      };
      const codeName = codeMap[error.code] ?? `UNKNOWN_CODE_${error.code}`;
      const nativeMessage = 'message' in error && typeof error.message === 'string'
        ? error.message
        : '';
      return nativeMessage ? `${codeName}: ${nativeMessage}` : codeName;
    };

    const logPrefix = '[ReelPlayer]';
    const logRequested = () => {
      console.log(`${logPrefix} Video fetch requested`, {
        videoUrl,
        readyState: video.readyState,
        networkState: video.networkState,
      });
    };
    const logSuccess = () => {
      console.log(`${logPrefix} Video fetch successful`, {
        videoUrl,
        readyState: video.readyState,
        networkState: video.networkState,
      });
    };
    const logFailure = () => {
      console.error(`${logPrefix} Video fetch failed`, {
        videoUrl,
        error: describeMediaError(video.error),
        readyState: video.readyState,
        networkState: video.networkState,
      });
    };

    video.addEventListener('loadstart', logRequested);
    video.addEventListener('loadeddata', logSuccess);
    video.addEventListener('canplay', logSuccess);
    video.addEventListener('error', logFailure);

    if (typeof isActive === 'boolean') {
      if (isActive) {
        video.play().catch(() => {});
        if (onVisible && !hasReportedVisible.current) {
          hasReportedVisible.current = true;
          onVisible();
        }
      } else {
        video.pause();
      }
      return () => {
        video.removeEventListener('loadstart', logRequested);
        video.removeEventListener('loadeddata', logSuccess);
        video.removeEventListener('canplay', logSuccess);
        video.removeEventListener('error', logFailure);
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        const isVisible = entry.isIntersecting;
        if (isVisible) {
          video.play().catch(() => {});
          if (onVisible && !hasReportedVisible.current) {
            hasReportedVisible.current = true;
            onVisible();
          }
        } else {
          video.pause();
        }
      },
      { threshold: 0.5, rootMargin: '0px' }
    );

    observer.observe(video);
    return () => {
      observer.disconnect();
      video.removeEventListener('loadstart', logRequested);
      video.removeEventListener('loadeddata', logSuccess);
      video.removeEventListener('canplay', logSuccess);
      video.removeEventListener('error', logFailure);
    };
  }, [videoUrl, onVisible, isActive]);

  return (
    <div className={className} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <video
        ref={videoRef}
        src={videoUrl}
        playsInline
        controls={false}
        muted
        loop
        preload="metadata"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </div>
  );
};

export default ReelPlayer;
